import {
  Token,
  ALLOWED_TAGS,
  TokenUnknownAttribute,
} from '@cigna/resource-content/contracts';

export enum Severity {
  Warning = 'warning',
  Error = 'error',
}

export interface ValidationMessage {
  message: string;
  severity: Severity;
}

const TAG_RULES: {
  [tag: string]: string[];
} = ALLOWED_TAGS.reduce((m: { [tag: string]: string[] }, r) => {
  r.tags.forEach((t) => (m[t] = r.attributes.map((s) => s.name)));
  return m;
}, {});

export function validate(tokens: Token[], maxDepth = 2): ValidationMessage[] {
  return validateTokens(tokens, maxDepth);
}

function validateTokens(
  tokens: Token[],
  maxDepth: number,
  nestingPath: string[] = [],
): ValidationMessage[] {
  const validationResult: ValidationMessage[] = [];

  tokens.forEach((token) => {
    if (typeof token === 'string') {
      return;
    }

    if (nestingPath.length > maxDepth) {
      validationResult.push({
        severity: Severity.Warning,
        message: `Too much nesting ${concatNestingPath(nestingPath, token).join(
          ' -> ',
        )}`,
      });
    }

    const tagRule = TAG_RULES[token[0]];

    if (!tagRule) {
      validationResult.push({
        severity: Severity.Warning,
        message: `Unsupported tag <${token[0]}>`,
      });
    }

    if (tagRule) {
      const unknownAttributes = token.slice(
        2 + tagRule.length,
      ) as TokenUnknownAttribute[];

      unknownAttributes.forEach(([attrName, attrValue]) =>
        validationResult.push({
          severity: Severity.Warning,
          message: `Unsupported attribute on <${token[0]}>: ${attrName}="${attrValue}"`,
        }),
      );
    }

    if (Array.isArray(token[1])) {
      const childrenValidationResult = validateTokens(
        token[1],
        maxDepth,
        concatNestingPath(nestingPath, token),
      );

      if (childrenValidationResult.length) {
        validationResult.push(...childrenValidationResult);
      }
    }
  });

  return validationResult;
}

function concatNestingPath(nestingPath: string[], token: Token) {
  return [...nestingPath, `<${token[0]}>`];
}
